import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Clock3,
  FileJson,
  FilePlus2,
  FolderOpen,
  Import,
  LockKeyhole,
  PencilLine,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { usePortal } from "../state/PortalContext";
import { AppSidebarPortal } from "../components/AppShell";
import { DashboardWorkspaceNav } from "../components/DashboardWorkspaceNav";
import { ProjectNameDialog } from "../components/ProjectNameDialog";
import { PaperKingdomProjectSchema } from "../domain/schema";
import { getMarketplaceGuide } from "../data/marketplaces";
import { getProjectCompletion } from "../lib/projectProgress";
import {
  AppSelect,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  getInitials,
} from "../components/ui";

function ImportProjectDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: (projectId: string) => void;
}) {
  const { importProject } = usePortal();
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chooseFileRef = useRef<HTMLButtonElement>(null);
  const [strategy, setStrategy] = useState<"keep-both" | "replace">("keep-both");
  const [importNotice, setImportNotice] = useState("");
  const [isImporting, setIsImporting] = useState(false);

  const closeDialog = () => {
    setImportNotice("");
    setStrategy("keep-both");
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    chooseFileRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  const handleImport = async (file?: File) => {
    if (!file) return;
    setIsImporting(true);
    setImportNotice("");

    try {
      let parsed: unknown;
      try {
        parsed = JSON.parse(await file.text());
      } catch {
        setImportNotice(
          "That file is not valid JSON. Choose a project backup exported from Paper Kingdom.",
        );
        return;
      }

      const validation = PaperKingdomProjectSchema.safeParse(parsed);
      if (!validation.success) {
        setImportNotice(
          "This is not a valid Paper Kingdom project backup. No local project was changed.",
        );
        return;
      }

      const result = importProject(validation.data, strategy);
      onImported(result.project.id);
    } catch {
      setImportNotice("Import could not be completed. No local project was changed.");
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) closeDialog();
      }}
    >
      <section
        className="confirm-dialog import-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header>
          <span className="confirm-dialog__icon import-dialog__icon" aria-hidden="true">
            <FileJson size={20} />
          </span>
          <div>
            <h2 id={titleId}>Import a project</h2>
            <p id={descriptionId}>
              Choose a Paper Kingdom JSON backup. It will be validated before anything changes.
            </p>
          </div>
          <button className="icon-button" type="button" aria-label="Close" onClick={closeDialog}>
            <X size={18} />
          </button>
        </header>
        <div className="import-dialog__body">
          <label className="field">
            <span>If this project already exists</span>
            <AppSelect
              value={strategy}
              aria-label="Import conflict strategy"
              onChange={(event) => setStrategy(event.target.value as "keep-both" | "replace")}
            >
              <option value="keep-both">Keep both projects</option>
              <option value="replace">Replace the existing project</option>
            </AppSelect>
            <small>
              Keeping both is safest. Replace uses the imported backup as the latest revision.
            </small>
          </label>
          <input
            ref={fileInputRef}
            className="visually-hidden"
            type="file"
            hidden
            tabIndex={-1}
            aria-hidden="true"
            accept="application/json,.json"
            onChange={(event) => void handleImport(event.target.files?.[0])}
          />
          {importNotice ? (
            <p className="import-notice" role="alert">
              {importNotice}
            </p>
          ) : null}
        </div>
        <footer>
          <Button variant="secondary" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            ref={chooseFileRef}
            disabled={isImporting}
            onClick={() => fileInputRef.current?.click()}
          >
            <Import size={16} />
            {isImporting ? "Importing…" : "Choose JSON file"}
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

export function StudioDashboard() {
  const {
    entitlements,
    projects,
    createProject,
    deleteProject,
    updateProject,
    storageMode,
  } = usePortal();
  const navigate = useNavigate();
  const hasCore = entitlements.includes("paper-kingdom-fe");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);

  const startProject = (name: string) => {
    const project = createProject(name);
    setCreateOpen(false);
    void navigate({ to: "/PaperKingdom/studio/projects/$projectId", params: { projectId: project.id } });
  };

  const openImportedProject = (projectId: string) => {
    setImportOpen(false);
    void navigate({ to: "/PaperKingdom/studio/projects/$projectId", params: { projectId } });
  };

  return (
    <>
      <AppSidebarPortal>
        <DashboardWorkspaceNav
          active="projects"
          projectCount={projects.length}
          cloudWorkspace={storageMode === "cloud"}
          hasResellerRights={entitlements.includes("paper-kingdom-reseller")}
          actions={hasCore ? (
            <div className="studio-home-sidebar__actions">
              <button className="studio-home-sidebar__new" type="button" onClick={() => setCreateOpen(true)}>
                <Plus size={16} /> New project
              </button>
              <button
                className="studio-home-sidebar__new studio-home-sidebar__new--secondary"
                type="button"
                onClick={() => setImportOpen(true)}
              >
                <Import size={16} /> Import project
              </button>
            </div>
          ) : null}
        />
      </AppSidebarPortal>

      <main className="studio-dashboard">
      {!hasCore ? (
        <EmptyState
          icon={<LockKeyhole size={24} />}
          title="Paper Kingdom Core is off"
          description="This account does not have the Core tools enabled. An administrator can enable Core access from the Publisher menu."
        />
      ) : (
        <section
          className={`dashboard-projects ${projects.length ? "" : "dashboard-projects--empty"}`}
          aria-label="Projects"
        >
          {projects.length ? (
            <div className="project-grid">
              {projects.map((project) => {
                const progress = getProjectCompletion(project, entitlements);
                const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
                const itemNoun = isBook ? "book" : "product";
                return (
                  <article className="project-card" key={project.id}>
                    <div className="project-card__top">
                      <span className="project-card__mark">
                        {getInitials(project.imprint.name)}
                      </span>
                      <div className="project-card__actions">
                        <button
                          className="icon-button icon-button--neutral"
                          type="button"
                          aria-label={`Rename ${project.name}`}
                          title="Rename project"
                          onClick={() => setRenameTarget({ id: project.id, name: project.name })}
                        >
                          <PencilLine size={16} />
                        </button>
                        <button
                          className="icon-button"
                          type="button"
                          aria-label={`Delete ${project.name}`}
                          title="Delete project"
                          onClick={() => setDeleteTarget({ id: project.id, name: project.name })}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="project-card__badges">
                        <Badge tone="green">{project.marketplace}</Badge>
                        <Badge>{project.format}</Badge>
                      </div>
                      <h3>{project.name}</h3>
                      <p>{project.theme || "Catalogue direction not set yet"}</p>
                    </div>
                    <div className="project-card__progress-label">
                      <span>{isBook ? "Book plan" : "Product plan"}</span>
                      <strong>{progress.planning.label}</strong>
                    </div>
                    <div className="progress-line" aria-label={`${progress.planning.percentage}% ${itemNoun}-plan progress`}>
                      <span style={{ width: `${progress.planning.percentage}%` }} />
                    </div>
                    <div className="project-card__meta">
                      <span>
                        <FilePlus2 size={15} /> {project.catalogue.length}{" "}
                        {project.catalogue.length === 1 ? itemNoun : `${itemNoun}s`}
                      </span>
                      <span><Clock3 size={15} /> {new Date(project.updatedAt).toLocaleDateString()}</span>
                    </div>
                    <Link
                      className="button button--secondary button--md"
                      to="/PaperKingdom/studio/projects/$projectId"
                      params={{ projectId: project.id }}
                    >
                      Continue project <ArrowRight size={16} />
                    </Link>
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={<FolderOpen size={24} />}
              title="Start with one product idea"
              description="Tell us who it is for and what it should help them do. You can add more products later."
            >
              <div className="empty-state__actions">
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus size={17} /> Create new project
                </Button>
                <Button variant="secondary" onClick={() => setImportOpen(true)}>
                  <Import size={17} /> Import project
                </Button>
              </div>
            </EmptyState>
          )}
        </section>
      )}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete “${deleteTarget?.name ?? "this project"}”?`}
        description="This removes the project and its saved prompt choices from this browser. Export a JSON backup first if you may need it again."
        confirmLabel="Delete project"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteProject(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
      <ProjectNameDialog
        open={createOpen}
        mode="create"
        onCancel={() => setCreateOpen(false)}
        onConfirm={startProject}
      />
      <ProjectNameDialog
        open={Boolean(renameTarget)}
        mode="rename"
        initialName={renameTarget?.name}
        onCancel={() => setRenameTarget(null)}
        onConfirm={(name) => {
          if (renameTarget) {
            updateProject(renameTarget.id, (current) => ({ ...current, name }));
          }
          setRenameTarget(null);
        }}
      />
      <ImportProjectDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={openImportedProject}
      />
      </main>
    </>
  );
}
